import { Action, Dispatch } from 'redux';
import { IAppState } from './reducers';
import {
  startBackgroundTask,
  stopBackgroundTask,
} from './jobStatus';
import {
  selectUserOrgIds,
  selectIsUserVaultSuperAdmin,
  selectIsUserExchangeAdmin,
  selectIsUserExchangeVendor,
} from './user/userOrgRole';
import { getProductNamesByOrgId } from '../utils/user';
import { getNamingTemplates, updateNamingTemplates } from '../api/vault/namingTemplates';
import { ActionType as TemplateAction } from './exchange/exporttemplateSettings';
import { init as initExchange } from './exchange/initExchange';
import { VAULT_VIDEO_TYPE } from '../constants';
import { loadOrgTags } from './tags';

export enum Product {
  Vault = 'Vault',
  MyVault = 'MyVault',
  Exchange = 'Exchange',
}

const AvailableProducts: Array<Product> = [
  Product.Vault,
  Product.MyVault,
  Product.Exchange,
];

export enum ActionType {
  ToggleOpen = 'Product.ToggleOpen',
  UpdateTarget = 'Product.UpdateTarget',
  UpdateAvailable = 'Product.UpdateAvailable',
  ExchangeTemplateNotification = 'Product.ExchangeTemplateNotification',
  CloseExchangeTemplateNotification = 'Product.CloseExchangeTemplateNotification',
}

export interface IProductAction extends Action<ActionType> {
  payload?: any;
}

export interface IState {
  isOpen: boolean;
  target: Product;
  available: Array<Product>;
  isExchangeTemplateNotificationOpen: boolean;
  checkedExchangeTemplateNotification: boolean;
  isVaultSuperAdmin: boolean;
}

const PRODUCT_STORAGE_KEY = 'catapult:product';

/**
 * The selected product is cached outside redux so it survives a reload. Exported because
 * ending an impersonation session reloads the page: the product the admin picked has to be
 * recorded before that reload, and dispatching the switch instead would only kick off data
 * loads under a token that is about to be dropped.
 */
export const cacheProduct = (target: Product) => localStorage.setItem(PRODUCT_STORAGE_KEY, target);

const INITIAL_STATE: IState = {
  isOpen: false,
  target: null,
  available: [],
  isExchangeTemplateNotificationOpen: false,
  checkedExchangeTemplateNotification: false,
  isVaultSuperAdmin: false,
};

export default function reducer(state: IState = INITIAL_STATE, action: IProductAction): IState {
  switch (action.type) {
    case ActionType.ToggleOpen:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.UpdateTarget:
      cacheProduct(action.payload.target);
      return {
        ...state,
        target: action.payload.target,
        isOpen: false,
      };
    case ActionType.UpdateAvailable:
      return {
        ...state,
        available: action.payload.available,
      };
    case ActionType.ExchangeTemplateNotification:
      return {
        ...state,
        isExchangeTemplateNotificationOpen: action.payload.isExchangeTemplateNotificationOpen,
        checkedExchangeTemplateNotification: action.payload.checkedExchangeTemplateNotification,
      };
    case ActionType.CloseExchangeTemplateNotification:
      return {
        ...state,
        isExchangeTemplateNotificationOpen: false,
      };
    default:
      return state;
  }
}

export const updateProduct = (target: Product, isVaultSuperAdmin: boolean) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const checkedExchangeTemplateNotification = state.product?.checkedExchangeTemplateNotification ?? false;
  const backgroundTask = state.jobStatus?.backgroundTask ?? -1;
  dispatch({
    type: ActionType.UpdateTarget,
    payload: {
      target,
    },
  });

  if (target === Product.Exchange) {
    await initExchange()(dispatch, getState);
  }

  // Check exchange template notification once on exchange screen
  if (target === Product.Exchange && !checkedExchangeTemplateNotification) {
    const response = await getNamingTemplates();
    const namingTemplate = response.data?.data;
    if (namingTemplate) {
      dispatch({
        type: ActionType.ExchangeTemplateNotification,
        payload: {
          isExchangeTemplateNotificationOpen: !!namingTemplate.notification,
          checkedExchangeTemplateNotification: true,
        },
      });
      // Tell api that notification has been opened
      await updateNamingTemplates(namingTemplate._id, {
        notification: false,
      });
    }
  }

  if (backgroundTask !== -1) stopBackgroundTask()(dispatch, getState);
  startBackgroundTask(getVideoTypeFromProduct(target), isVaultSuperAdmin)(dispatch, getState);
};

export const getAvailableProducts = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const orgIds = selectUserOrgIds(state);

  // VA-3110 Need to add for tag filter in search results
  // Remove once fix is in
  await loadOrgTags()(dispatch, getState);

  try {
    const results = await getProductNamesByOrgId(orgIds);
    const available = results.filter((product) => AvailableProducts.includes(product as Product));

    dispatch({
      type: ActionType.UpdateAvailable,
      payload: {
        available,
      },
    });

    const cachedProduct = localStorage.getItem(PRODUCT_STORAGE_KEY);

    const isVaultSuperAdmin = selectIsUserVaultSuperAdmin(state);
    const isExchangeAdmin = selectIsUserExchangeAdmin(state);
    const isExchangeVendor = selectIsUserExchangeVendor(state);

    if (!cachedProduct || available.indexOf(cachedProduct) === -1) {
      // note: if a product hasn't previously been cached or the cached permission is invalid, update according to user role
      let target: Product;
      if ((isExchangeAdmin || isExchangeVendor) && available.includes(Product.Exchange)) {
        target = Product.Exchange;
      } else if (isVaultSuperAdmin && available.includes(Product.Vault)) {
        target = Product.Vault;
      } else if (available.includes(Product.MyVault)) {
        target = Product.MyVault;
      } else if (available.length >= 1) {
        const [prod] = available;
        target = prod as Product;
      }
      updateProduct(target, isVaultSuperAdmin)(dispatch, getState);
    } else {
      updateProduct(cachedProduct as Product, isVaultSuperAdmin)(dispatch, getState);
    }
  } catch (e) {
    console.error('[QA] An error occurred while fetching user org products', e.toString());
  }
};

export const toggleOpen = () => ({
  type: ActionType.ToggleOpen,
});

export const closeExchangeTemplateNotificationAction = (openTemplate: boolean) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.CloseExchangeTemplateNotification,
    payload: {},
  });

  if (openTemplate) {
    dispatch({
      type: TemplateAction.Toggle,
    });
  }
};

export const selectIsExchange = (state: IAppState) => state.product.target === Product.Exchange;

export const getVideoTypeFromProduct = (product: Product) => (product === Product.Exchange ? [VAULT_VIDEO_TYPE.Exchange] : [VAULT_VIDEO_TYPE.Clip, 'clips', VAULT_VIDEO_TYPE.MyVault, VAULT_VIDEO_TYPE.Vault]);
